import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import LoginLayer from "./components/LoginLayer";
import WelcomePage from "./components/WelcomePage";
import EcosystemBanner from "./components/EcosystemBanner";
import Dashboard from "./components/Dashboard";
import PublicEventPage from "./components/PublicEventPage";
import AuthCallback from "./components/AuthCallback";
import { getToken, clearAuth, setStoredUser, getStoredUser } from "./lib/api";
import api from "./lib/api";

function MainApp() {
  const location = useLocation();
  // Read hash from useLocation (reactive) not window.location.hash (not reactive)
  // as per Emergent Auth playbook — required so React re-renders after AuthCallback
  // clears the fragment via history.replaceState.
  const hasOAuthFragment = location.hash?.includes("session_id=");

  const [authed, setAuthed] = useState(false);
  const [currentUser, setCurrentUser] = useState(getStoredUser());
  const [checking, setChecking] = useState(true);
  const [showLogin, setShowLogin] = useState(false);

  // Show toast when Google Calendar redirect returns
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cal = params.get("calendar");
    if (cal === "connected") {
      const email = params.get("email") || "";
      toast.success(`Google Calendar collegato ${email ? `(${email})` : ""} ⚡`);
      const url = new URL(window.location.href);
      url.searchParams.delete("calendar");
      url.searchParams.delete("email");
      window.history.replaceState({}, "", url.toString());
    } else if (cal === "error") {
      toast.error("Collegamento Google Calendar non riuscito. Riprova.");
      const url = new URL(window.location.href);
      url.searchParams.delete("calendar");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.toString());
    }
  }, [location.search]);

  useEffect(() => {
    if (hasOAuthFragment) { setChecking(false); return; }
    const token = getToken();
    if (!token) { setChecking(false); return; }
    api.get("/auth/user-me")
      .then((res) => {
        setStoredUser(res.data.user);
        setCurrentUser(res.data.user);
        setAuthed(true);
      })
      .catch(() => {
        return api.post("/auth/verify")
          .then(() => {
            setCurrentUser(null);
            setAuthed(true);
          })
          .catch(() => clearAuth());
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasOAuthFragment) {
    return <AuthCallback onSuccess={() => setAuthed(true)} />;
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F6]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#FF7C43] border-t-transparent" />
      </div>
    );
  }

  if (authed) {
    return (
      <>
        <EcosystemBanner />
        <Dashboard currentUser={currentUser} onLogout={() => {
          setAuthed(false);
          setCurrentUser(null);
          setShowLogin(false);
        }} />
      </>
    );
  }

  // Not authed: show WelcomePage by default; LoginLayer on demand
  if (showLogin) {
    return (
      <>
        <EcosystemBanner />
        <LoginLayer
          onSuccess={() => {
            setAuthed(true);
            setCurrentUser(getStoredUser());
            setShowLogin(false);
          }}
          onBack={() => setShowLogin(false)}
          key={location.pathname}
        />
      </>
    );
  }

  return (
    <>
      <EcosystemBanner />
      <WelcomePage onLogin={() => setShowLogin(true)} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "white",
            border: "1px solid #E8E1D9",
            color: "#2D2926",
            borderRadius: "16px",
            fontFamily: "Outfit, sans-serif",
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/event/:publicId" element={<PublicEventPage />} />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
